// Transactions route — the second interactive page. Filters/search are derived
// (re-render the list); per-card editors (category typeahead, split editor,
// action menu) are transient and driven by a page-level openState signal so only
// the affected card's region re-renders. Live split remainder/validation use the
// signal + computed pattern (focus-preserving). All filtering/split/category
// logic reused unchanged from the shared lib; mutations are optimistic with
// undo toasts.
import { h, mount, type Child } from '../core/dom';
import { signal, computed, effect, type Signal } from '../core/signal';
import { bindInput, bindChecked, text, toggleClass } from '../core/bind';
import { getCurrentMonth, changeMonth, isValidMonthKey } from '@lib/month';
import { navigate } from '../core/router';
import {
	filterTransactions,
	getCategoryOptions,
	txOffset,
	derivedMonthOf,
	shortMonthLabel,
	isSplit,
	splitOutOfDate,
	type TransactionView,
	type TxFilter,
} from '@lib/transactionFilter';
import {
	splitRemainder,
	validateSplitLines,
	distributeRemainder,
	rowsToSplitLines,
	buildSplitPayload,
	type SplitRow,
} from '@lib/split';
import { UNCAT, isUncategorised, displayCategoryName } from '@lib/category';
import { formatCurrency } from '@lib/format';
import { formatError, type Category } from '@lib/db';
import {
	loadTransactionsData,
	updateTransactionCategory,
	setTransactionExcludeFromBudget,
	setTransactionMonthOffset,
	setTransactionSplits,
	clearTransactionSplits,
} from '../data';
import { monthNav } from '../components/monthNav';
import { setHeaderControl } from '../components/headerSlot';
import { toast } from '../ui/toast';
import { loading } from '../ui/loading';

type Sig = Signal<string>;
interface SplitRowModel {
	id: number;
	category: Sig;
	amount: Sig;
	note: Sig;
	exclude: Signal<boolean>;
}
type OpenState = { sk: string; what: 'menu' | 'split' | 'actions' } | null;

const TABS: { key: TxFilter; label: string }[] = [
	{ key: 'all', label: 'All' },
	{ key: 'uncategorised', label: 'Uncategorised' },
	{ key: 'excluded', label: 'Excluded' },
	{ key: 'transfers', label: 'Transfers' },
];

export function transactionsRoute(query?: URLSearchParams): Node {
	const container = h('div', {});
	const qMonth = query?.get('month');
	const month = isValidMonthKey(qMonth) ? qMonth : getCurrentMonth();
	const initialCategory = query?.get('category') ?? null;
	// Month changes navigate (URL is the source of truth), preserving any
	// category drill-down; the router re-renders the route on hashchange.
	const goto = (off: number) => {
		const next = changeMonth(month, off);
		const cat = catFilter() ? `&category=${encodeURIComponent(catFilter()!)}` : '';
		navigate(`/transactions?month=${next}${cat}`);
	};
	// Category drill-down (?category=...) — cleared by picking any filter tab.
	const catFilter = signal<string | null>(initialCategory);
	render();
	return container;

	// Rewrite the hash without the category param (replaceState fires no
	// hashchange, so the route isn't re-mounted — mirrors Svelte's clear-filter).
	function stripCategoryFromUrl(): void {
		const hash = location.hash.slice(1) || '/';
		const qIdx = hash.indexOf('?');
		if (qIdx === -1) return;
		const path = hash.slice(0, qIdx);
		const params = new URLSearchParams(hash.slice(qIdx + 1));
		if (!params.has('category')) return;
		params.delete('category');
		const qs = params.toString();
		history.replaceState(null, '', `#${path}${qs ? `?${qs}` : ''}`);
	}

	function clearCategoryFilter(): void {
		catFilter.set(null);
		stripCategoryFromUrl();
	}

	function render(): void {
		setHeaderControl(monthNav(month, goto));
		container.replaceChildren(loading('Loading transactions…'));
		loadTransactionsData(month)
			.then(({ transactions, categories }) => build(transactions, categories))
			.catch((e) => container.replaceChildren(h('div', { class: 'state-error' }, formatError(e as Error))));
	}

	function build(loaded: TransactionView[], categories: Category[]): void {
		const txs = signal<TransactionView[]>(loaded);
		const filter = signal<TxFilter>(initialCategory ? 'all' : 'uncategorised');
		const searchQuery = signal('');
		const open = signal<OpenState>(null);

		// Close transient menus on outside clicks (mirrors Svelte's window click
		// handler). The split editor only closes explicitly. The listener
		// self-removes once this route's DOM is unmounted.
		const onDocClick = (e: MouseEvent) => {
			if (!container.isConnected) {
				document.removeEventListener('click', onDocClick);
				return;
			}
			const o = open();
			if (!o || o.what === 'split') return;
			const t = e.target as Element | null;
			if (t && (t.closest('.category-menu') || t.closest('.action-menu') || t.closest('.tx-actions'))) return;
			open.set(null);
		};
		document.addEventListener('click', onDocClick);
		let splitCounter = 0;
		let splitRows = signal<SplitRowModel[]>([]); // active split editor rows

		const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));

		const filtered = computed(() =>
			filterTransactions(txs(), { filter: filter(), categoryFilter: catFilter(), searchQuery: searchQuery() })
		);
		const count = (f: TxFilter) =>
			filterTransactions(txs(), { filter: f, categoryFilter: null, searchQuery: '' }).length;

		function replaceTx(sk: string, patch: Partial<TransactionView>): void {
			txs.update((list) => list.map((t) => (t.sk === sk ? { ...t, ...patch } : t)));
		}

		// --- mutations (optimistic + undo) ---
		function changeCategory(tx: TransactionView, next: string): void {
			const prev = tx.category;
			open.set(null);
			replaceTx(tx.sk, { category: next });
			updateTransactionCategory(tx.pk, tx.sk, next).catch((e) => {
				replaceTx(tx.sk, { category: prev });
				toast(formatError(e as Error));
			});
			toast(`Categorised as ${displayCategoryName(next)}`, {
				undo: () => {
					replaceTx(tx.sk, { category: prev });
					updateTransactionCategory(tx.pk, tx.sk, prev).catch((e) => {
						replaceTx(tx.sk, { category: next });
						toast(formatError(e as Error));
					});
				},
			});
		}
		function toggleExclude(tx: TransactionView): void {
			const next = !tx.excludeFromBudget;
			open.set(null);
			replaceTx(tx.sk, { excludeFromBudget: next });
			setTransactionExcludeFromBudget(tx.pk, tx.sk, next).catch((e) => {
				replaceTx(tx.sk, { excludeFromBudget: !next });
				toast(formatError(e as Error));
			});
		}
		let moveBusy = false; // guard against double-submitting a move
		function moveMonth(tx: TransactionView, offset: -1 | 0 | 1): void {
			if (moveBusy) return;
			moveBusy = true;
			const prev = txOffset(tx) as -1 | 0 | 1;
			open.set(null);
			setTransactionMonthOffset(tx.pk, tx.sk, offset)
				.then(() => {
					toast(offset === 0 ? 'Restored to natural month' : `Moved to ${shortMonthLabel(changeMonth(month, offset))}`, {
						undo: () => {
							setTransactionMonthOffset(tx.pk, tx.sk, prev)
								.then(render)
								.catch((e) => toast(formatError(e as Error)));
						},
					});
					render();
				})
				.catch((e) => toast(formatError(e as Error)))
				.finally(() => (moveBusy = false));
		}
		function removeSplit(tx: TransactionView): void {
			open.set(null);
			clearTransactionSplits(tx.pk, tx.sk)
				.then(() => replaceTx(tx.sk, { splits: undefined, category: UNCAT }))
				.catch((e) => toast(formatError(e as Error)));
		}

		function openSplit(tx: TransactionView): void {
			const seed: SplitRow[] =
				tx.splits && tx.splits.length >= 2
					? tx.splits.map((s, i) => ({ id: i, category: s.category, amount: String(s.amount), note: s.note ?? '', excludeFromBudget: !!s.excludeFromBudget }))
					: [
							{ id: 0, category: tx.category, amount: String(tx.amount), note: '', excludeFromBudget: false },
							{ id: 1, category: UNCAT, amount: '0', note: '', excludeFromBudget: false },
						];
			splitCounter = seed.length;
			splitRows = signal(seed.map(rowModel));
			open.set({ sk: tx.sk, what: 'split' });
		}
		function rowModel(r: SplitRow): SplitRowModel {
			return { id: r.id, category: signal(r.category), amount: signal(r.amount), note: signal(r.note), exclude: signal(r.excludeFromBudget) };
		}
		function toRow(m: SplitRowModel): SplitRow {
			return { id: m.id, category: m.category(), amount: m.amount(), note: m.note(), excludeFromBudget: m.exclude() };
		}

		// --- render ---
		container.replaceChildren(
			h(
				'div',
				{},
				categoryBanner(),
				toolbar(),
				listView()
			)
		);

		function categoryBanner(): Child {
			const box = h('div', {});
			mount(box, () => {
				const cat = catFilter();
				if (!cat) return null;
				return h(
					'div',
					{ class: 'category-filter-banner' },
					h('span', {}, 'Showing: ', h('strong', {}, displayCategoryName(cat))),
					h('button', { class: 'clear-filter', onclick: clearCategoryFilter }, 'Clear filter')
				);
			});
			return box;
		}

		function toolbar(): Child {
			const search = h('input', { class: 'input', type: 'search', placeholder: 'Search…', 'aria-label': 'Search transactions' }) as HTMLInputElement;
			let timer: ReturnType<typeof setTimeout>;
			search.addEventListener('input', () => {
				clearTimeout(timer);
				timer = setTimeout(() => searchQuery.set(search.value.trim().toLowerCase()), 200);
			});
			const tabs = h(
				'div',
				{ class: 'tabs' },
				...TABS.map((t) => {
					const btn = h(
						'button',
						{ class: 'tab', onclick: () => { clearCategoryFilter(); filter.set(t.key); open.set(null); } },
						text(() => `${t.label} (${count(t.key)})`)
					);
					toggleClass(btn, 'active', () => filter() === t.key && !catFilter());
					return btn;
				})
			);
			return h('div', { class: 'toolbar' }, tabs, search);
		}

		function emptyMessage(): string {
			if (searchQuery()) return 'No transactions match your search';
			switch (filter()) {
				case 'uncategorised': return 'All transactions are categorised!';
				case 'excluded': return 'No excluded transactions this month';
				case 'transfers': return 'No transfers this month';
				default: return 'No transactions this month';
			}
		}

		function listView(): Child {
			const list = h('div', { class: 'tx-list' });
			mount(list, () => {
				const rows = filtered();
				if (rows.length === 0) return h('p', { class: 'muted' }, emptyMessage());
				return rows.map((tx) => txCard(tx));
			});
			return list;
		}

		function txCard(tx: TransactionView): Child {
			const region = h('div', {});
			mount(region, () => {
				const o = open();
				if (o?.sk !== tx.sk) return null;
				if (o.what === 'menu') return categoryMenu(tx, catBtn);
				if (o.what === 'split') return splitEditor(tx);
				if (o.what === 'actions') return actionMenu(tx, actionsBtn);
				return null;
			});

			const catBtn = h(
				'button',
				{
					class: 'btn btn-sm btn-secondary',
					'aria-haspopup': 'listbox',
					onclick: () => open.set(open()?.sk === tx.sk && open()?.what === 'menu' ? null : { sk: tx.sk, what: 'menu' }),
				},
				displayCategoryName(tx.category)
			);
			const actionsBtn = h(
				'button',
				{
					class: 'btn btn-sm btn-secondary',
					'aria-label': 'Actions',
					'aria-haspopup': 'menu',
					onclick: () => open.set(open()?.sk === tx.sk && open()?.what === 'actions' ? null : { sk: tx.sk, what: 'actions' }),
				},
				'⋯'
			);
			effect(() => catBtn.setAttribute('aria-expanded', String(open()?.sk === tx.sk && open()?.what === 'menu')));
			effect(() => actionsBtn.setAttribute('aria-expanded', String(open()?.sk === tx.sk && open()?.what === 'actions')));

			// Card state accents (transfer/excluded/pending/suggestion), matching the
			// Svelte TransactionCard's visual hierarchy.
			const cls = ['tx-card'];
			if (tx.isTransfer) cls.push('is-transfer');
			if (tx.excludeFromBudget) cls.push('is-excluded');
			if (tx.pending) cls.push('is-pending');
			if (tx.suggestedCategory && isUncategorised(tx.category)) cls.push('has-suggestion');

			return h(
				'div',
				{ class: cls.join(' ') },
				h(
					'div',
					{ class: 'tx-head' },
					h('span', { class: 'tx-date' }, tx.date),
					...badges(tx),
					h('span', { class: `tx-amount ${tx.amount < 0 ? 'negative' : 'positive'}` }, formatCurrency(tx.amount))
				),
				h('div', { class: 'tx-desc' }, tx.merchant?.name || tx.description),
				// Raw bank description (when a merchant name is shown) + bank meta
				// (particulars | code | reference), matching the Svelte card.
				tx.merchant?.name && tx.description !== tx.merchant.name ? h('div', { class: 'tx-details' }, tx.description) : null,
				(() => {
					const meta = [tx.meta?.particulars, tx.meta?.code, tx.meta?.reference].filter(Boolean);
					return meta.length ? h('div', { class: 'tx-meta' }, meta.join(' | ')) : null;
				})(),
				suggestionBanner(tx),
				splitView(tx),
				h('div', { class: 'tx-actions' }, catBtn, actionsBtn),
				region
			);
		}

		function badges(tx: TransactionView): Child[] {
			const out: Child[] = [];
			const add = (cls: string, label: string) => out.push(h('span', { class: `badge ${cls}` }, label));
			if (tx.pending) add('pending', 'Pending');
			if (tx.isTransfer) add('transfer', 'Transfer');
			if (tx.excludeFromBudget) add('excluded', 'Excluded');
			if (tx.autoCategorised) add('auto', 'Auto');
			if (isSplit(tx)) add('split', 'Split');
			const off = txOffset(tx);
			if (off !== 0) add('shifted', `Shifted ${off > 0 ? '+' : ''}${off}`);
			return out;
		}

		function suggestionBanner(tx: TransactionView): Child {
			if (!tx.suggestedCategory || !isUncategorised(tx.category)) return null;
			return h(
				'div',
				{ class: 'suggestion' },
				h('span', {}, `Suggested: ${tx.suggestedCategory} (${Math.round((tx.suggestedConfidence ?? 0) * 100)}%)`),
				h('button', { class: 'btn btn-sm btn-primary', onclick: () => changeCategory(tx, tx.suggestedCategory!) }, 'Accept')
			);
		}

		function splitView(tx: TransactionView): Child {
			if (!isSplit(tx) || (open()?.sk === tx.sk && open()?.what === 'split')) return null;
			return h(
				'div',
				{ class: 'split-view' },
				splitOutOfDate(tx) ? h('div', { class: 'split-warning' }, 'Split is out of date — re-split to fix.') : null,
				h('ul', { class: 'split-lines' }, ...(tx.splits ?? []).map((s) =>
					h(
						'li',
						{},
						h('span', {}, s.excludeFromBudget ? 'Transfer' : s.category, s.note ? h('span', { class: 'split-line-note' }, ` ${s.note}`) : null),
						h('span', {}, formatCurrency(s.amount))
					)
				))
			);
		}

		// --- category typeahead ---
		function categoryMenu(tx: TransactionView, trigger?: HTMLElement): Child {
			const query = signal('');
			const highlight = signal(0);
			const optionsOf = () => getCategoryOptions(sorted, query(), tx.amount);

			const input = h('input', {
				class: 'input',
				role: 'combobox',
				'aria-autocomplete': 'list',
				'aria-controls': 'cat-menu-options',
				placeholder: !isUncategorised(tx.category) ? displayCategoryName(tx.category) : 'Select category',
				ref: (el) => queueMicrotask(() => (el as HTMLInputElement).focus()),
			}) as HTMLInputElement;
			bindInput(input, query);
			effect(() => input.setAttribute('aria-activedescendant', `cat-opt-${highlight()}`));
			input.addEventListener('keydown', (e) => {
				const opts = optionsOf();
				// Arrow keys wrap at the ends, matching the Svelte CategoryMenu.
				if (e.key === 'ArrowDown') { e.preventDefault(); if (opts.length) highlight.set((highlight() + 1) % opts.length); }
				else if (e.key === 'ArrowUp') { e.preventDefault(); if (opts.length) highlight.set((highlight() - 1 + opts.length) % opts.length); }
				else if (e.key === 'Enter') { e.preventDefault(); const o = opts[highlight()]; if (o) changeCategory(tx, o.value); }
				else if (e.key === 'Escape') {
					open.set(null);
					trigger?.focus();
				}
			});

			const optList = h('ul', { class: 'menu-options', role: 'listbox', id: 'cat-menu-options' });
			mount(optList, () =>
				optionsOf().map((o, i) => {
					const li = h('li', { id: `cat-opt-${i}`, class: o.suggested ? 'suggested' : 'muted', role: 'option', onmousedown: () => changeCategory(tx, o.value), onmouseenter: () => highlight.set(i) }, o.label);
					toggleClass(li, 'active', () => highlight() === i);
					effect(() => li.setAttribute('aria-selected', String(highlight() === i)));
					return li;
				})
			);
			return h('div', { class: 'category-menu' }, input, optList);
		}

		// --- action menu ---
		function actionMenu(tx: TransactionView, trigger?: HTMLElement): Child {
			const item = (label: string, onClick: () => void) => h('button', { class: 'menu-item', role: 'menuitem', onclick: onClick }, label);
			const off = txOffset(tx);
			return h(
				'div',
				{
					class: 'action-menu',
					role: 'menu',
					onkeydown: (e: KeyboardEvent) => {
						if (e.key === 'Escape') {
							open.set(null);
							trigger?.focus();
						}
					},
				},
				item('Move to previous month', () => moveMonth(tx, -1)),
				item('Move to next month', () => moveMonth(tx, 1)),
				off !== 0 ? item('Restore to natural month', () => moveMonth(tx, 0)) : null,
				item(tx.excludeFromBudget ? 'Include in budget' : 'Exclude from budget', () => toggleExclude(tx)),
				item(isSplit(tx) ? 'Edit split…' : 'Split…', () => openSplit(tx)),
				isSplit(tx) ? item('Remove split', () => removeSplit(tx)) : null
			);
		}

		// --- split editor (live) ---
		function splitEditor(tx: TransactionView): Child {
			const lines = () => rowsToSplitLines(splitRows().map(toRow));
			const remainder = computed(() => splitRemainder(tx.amount, lines()));
			const errorMsg = computed(() => validateSplitLines(tx.amount, lines()));
			const catOpts = getCategoryOptions(sorted, '', tx.amount).filter((o) => o.value !== UNCAT);

			const rowsBox = h('div', { class: 'split-editor' });
			mount(rowsBox, () => splitRows().map((m) => splitRowView(m)));

			const remEl = h('span', { class: 'split-remainder' }, text(() => `Remaining: ${formatCurrency(remainder())}`));
			toggleClass(remEl, 'balanced', () => remainder() === 0);

			const saveBtn = h('button', { class: 'btn btn-sm btn-primary', onclick: () => doSave() }, 'Save split') as HTMLButtonElement;
			const errEl = h('span', { class: 'state-error' }, text(() => errorMsg() ?? ''));
			toggleClass(errEl, 'hidden', () => !errorMsg());
			// Keep save disabled while the split is invalid.
			effect(() => (saveBtn.disabled = errorMsg() !== null));

			function doSave(): void {
				const built = rowsToSplitLines(splitRows().map(toRow));
				if (validateSplitLines(tx.amount, built)) return;
				const payload = buildSplitPayload(built);
				saveBtn.disabled = true; // in-flight guard; re-enabled by the error effect
				setTransactionSplits(tx.pk, tx.sk, payload)
					.then(() => {
						replaceTx(tx.sk, { splits: payload });
						open.set(null);
					})
					.catch((e) => {
						saveBtn.disabled = false;
						toast(formatError(e as Error));
					});
			}

			return h(
				'div',
				{ class: 'split-edit-wrap' },
				h('div', { class: 'split-edit-title' }, `Split ${formatCurrency(tx.amount)}`),
				rowsBox,
				h(
					'button',
					{ class: 'btn btn-sm btn-secondary', onclick: () => { splitRows.update((rs) => [...rs, rowModel({ id: splitCounter++, category: UNCAT, amount: '0', note: '', excludeFromBudget: false })]); } },
					'+ Add line'
				),
				h(
					'div',
					{ class: 'row' },
					remEl,
					h('button', { class: 'btn btn-sm btn-link', onclick: () => distribute() }, 'Distribute')
				),
				errEl,
				h('div', { class: 'row' }, saveBtn, h('button', { class: 'btn btn-sm btn-secondary', onclick: () => open.set(null) }, 'Cancel'))
			);

			function distribute(): void {
				const out = distributeRemainder(tx.amount, lines());
				const rows = splitRows();
				if (rows.length) rows[rows.length - 1].amount.set(String(out[out.length - 1].amount));
			}

			function splitRowView(m: SplitRowModel): Child {
				const cat = h('select', { class: 'select', 'aria-label': 'Split category' }) as HTMLSelectElement;
				cat.append(h('option', { value: UNCAT }, 'Category…'));
				for (const o of catOpts) cat.append(h('option', { value: o.value }, o.label));
				bindInput(cat, m.category, 'change');
				const amt = h('input', { class: 'input', inputmode: 'decimal', placeholder: '0.00', 'aria-label': 'Split amount' }) as HTMLInputElement;
				bindInput(amt, m.amount);
				const note = h('input', { class: 'input', placeholder: 'Note', 'aria-label': 'Split note' }) as HTMLInputElement;
				bindInput(note, m.note);
				const xfer = h('input', { type: 'checkbox' }) as HTMLInputElement;
				bindChecked(xfer, m.exclude);
				const rm = h('button', { class: 'x-btn', 'aria-label': 'Remove line', onclick: () => splitRows.update((rs) => (rs.length > 2 ? rs.filter((x) => x.id !== m.id) : rs)) }, '×');
				return h('div', { class: 'split-row' }, cat, amt, note, h('label', { class: 'row' }, xfer, 'Transfer'), rm);
			}
		}
	}
}
